import React from 'react';
import {
  DocPage, PageHeader, Section, SubSection, Prerequisites,
  Para, Callout,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './Contract.css';

const TOC = [
  { id: 'auto-renew',       label: 'Auto-Renew' },
  { id: 'discount',         label: 'Contract Discount' },
  { id: 'min-max',          label: 'Min / Max Limits' },
  { id: 'charge-sequence',  label: 'Charge Sequence' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDiscount(d, currency) {
  return d.type === 'percentage' ? `${d.value}%` : `${currency}${d.value.toLocaleString()}`;
}

// ── Min/max block ─────────────────────────────────────────────────────────────

function MinMaxBlock({ label, min, max, currency, note }) {
  const fmt = (v) => v != null ? `${currency}${v.toLocaleString()}` : '—';
  return (
    <div className="ctr-minmax">
      <div className="ctr-minmax-label">{label}</div>
      <div className="ctr-minmax-row">
        <div className="ctr-minmax-cell">
          <span className="ctr-minmax-key">Min</span>
          <span className="ctr-minmax-val">{fmt(min)}</span>
        </div>
        <div className="ctr-minmax-cell">
          <span className="ctr-minmax-key">Max</span>
          <span className="ctr-minmax-val">{fmt(max)}</span>
        </div>
      </div>
      {note && <div className="ctr-minmax-note">{note}</div>}
    </div>
  );
}

// ── Charge sequence ───────────────────────────────────────────────────────────

function ChargeSequence() {
  const steps = [
    { label: 'Usage',                       note: 'Actual (postpaid) or estimated (prepaid)' },
    { label: 'Pricing Model',               note: 'Fixed / Per Unit / Graduated / Volume / Min-Max' },
    { label: 'Proration',                   note: 'If enabled on the rate card' },
    { label: 'Fee Discount',      note: 'Applies to the current billing period band' },
    { label: 'Sum all components',          note: null, divider: true },
    { label: 'Billing Period Min / Max',    note: 'On included components only' },
    { label: 'Contract Discount (pre-tax)', note: 'If timing = pre-tax' },
    { label: 'Tax',                         note: 'Selected taxes per fee' },
    { label: 'Contract Discount (post-tax)',note: 'If timing = post-tax' },
    { label: 'Update cumulative total',     note: 'Contract Min / Max tracked here', final: true },
  ];

  return (
    <div className="ctr-sequence">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          {s.divider && (
            <div className="ctr-sequence-divider">per component ↑ &nbsp;&nbsp; across contract ↓</div>
          )}
          <div className={`ctr-sequence-step${s.final ? ' ctr-sequence-step--final' : ''}`}>
            <div className="ctr-sequence-label">{s.label}</div>
            {s.note && <div className="ctr-sequence-note">{s.note}</div>}
          </div>
          {!s.divider && i < steps.length - 1 && !steps[i + 1]?.divider && (
            <div className="ctr-sequence-arrow">↓</div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ContractSettings({ onNavigate }) {
  const { industry } = useIndustry();
  const c = industry.contract;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Contracts"
        title="Limits & Renewal"
        description="Contract-level settings that apply across all fees for the full contract duration — auto-renewal, a contract-wide discount, and cumulative or per-period billing limits."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'contract', label: 'Contracts' },
        ]}
      />

      <Section id="auto-renew" title="Auto-Renew">
        <Para>
          When enabled, the contract automatically creates a new term when the current one expires.
          A notification email is sent to the customer before renewal using a template that can be
          edited per contract.
        </Para>
        <Para>
          When disabled, billing stops at the contract end date. Usage events after expiry are not
          processed until a new contract is activated.
        </Para>
        <div className="ctr-level-row">
          <span className="ctr-level-label">This contract</span>
          <span className={`ctr-pill ${c.autoRenew ? 'ctr-pill--green' : 'ctr-pill--gray'}`}>
            {c.autoRenew ? 'Auto-renew on' : 'Auto-renew off'}
          </span>
        </div>
      </Section>

      <Section id="discount" title="Contract Discount">
        <Para>
          A single flat discount applied to the invoice total, on top of any fee discounts.
          Unlike component discounts, it does not change over time — it applies the same way every billing period.
        </Para>
        <table className="ctr-table">
          <thead>
            <tr><th>Setting</th><th>Options</th><th>Effect</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Type</td>
              <td>Fixed amount or percentage</td>
              <td>How the discount value is interpreted</td>
            </tr>
            <tr>
              <td>Timing</td>
              <td>Pre-tax or post-tax</td>
              <td>
                Pre-tax reduces the taxable base — the customer pays less tax.
                Post-tax is applied after tax is calculated — the tax amount is unchanged.
              </td>
            </tr>
          </tbody>
        </table>
        {c.contractDiscount && (
          <div className="ctr-level-row">
            <span className="ctr-level-label">This contract</span>
            <div className="ctr-level-pills">
              <span className="ctr-pill">{fmtDiscount(c.contractDiscount, c.currency)}</span>
              <span className="ctr-pill ctr-pill--gray">{c.contractDiscount.type}</span>
              <span className="ctr-pill ctr-pill--gray">{c.contractDiscount.timing}</span>
            </div>
          </div>
        )}
      </Section>

      <Section id="min-max" title="Min / Max Limits">
        <Para>
          Contracts support guardrails at two independent levels — per billing period and
          cumulative across the contract lifetime. Both can be active simultaneously.
        </Para>

        <SubSection title="Billing period min / max">
          <Para>
            Checked at the end of every billing period. Only the fees selected
            for inclusion count toward this check — others are excluded.
          </Para>
          <table className="ctr-table">
            <thead>
              <tr><th>Condition</th><th>What happens</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Total below minimum</td>
                <td>An upward adjustment line is added to the invoice to reach the minimum</td>
              </tr>
              <tr>
                <td>Total above maximum</td>
                <td>A downward adjustment line is added to the invoice to cap at the maximum</td>
              </tr>
            </tbody>
          </table>
        </SubSection>

        <SubSection title="Contract min / max (cumulative)">
          <Para>
            Tracks the running total across all billing periods for the life of the contract.
          </Para>
          <table className="ctr-table">
            <thead>
              <tr><th>Condition</th><th>What happens</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Running total hits the max</td>
                <td>All subsequent billing periods are capped — the customer stops being charged</td>
              </tr>
              <tr>
                <td>Total at contract end is below min</td>
                <td>A single catch-up invoice is generated for the shortfall</td>
              </tr>
            </tbody>
          </table>
        </SubSection>

        <Callout type="info" title="Contract min / max takes precedence">
          When both guardrails are active and they conflict, the contract-level limit wins.
          If the cumulative max has been reached, the period is zeroed out even if the billing
          period minimum would otherwise require a charge. The contract ceiling cannot be
          exceeded to satisfy a period floor.
        </Callout>

        <div className="ctr-minmax-group">
          <MinMaxBlock
            label="Billing Period Min / Max"
            min={c.billingPeriodMinMax.min}
            max={c.billingPeriodMinMax.max}
            currency={c.currency}
            note={`Applies to: ${c.billingPeriodMinMax.includedComponents.join(', ')}`}
          />
          <MinMaxBlock
            label="Contract Min / Max"
            min={c.contractMinMax.min}
            max={c.contractMinMax.max}
            currency={c.currency}
            note="Tracked cumulatively across all billing periods"
          />
        </div>
      </Section>

      <Section id="charge-sequence" title="Charge Sequence">
        <Para>
          For every billing period, charges flow through these steps in order:
        </Para>
        <ChargeSequence />
        <Callout type="info">
          The divider marks the boundary between per-component and cross-contract steps.
          Above it, each component is calculated independently. Below it, the results are
          combined and contract-level rules are applied.
        </Callout>
      </Section>

    </DocPage>
  );
}
